/**
 * Resume coordinator (byte-identical in owner + reception).
 *
 * Collapses the browser's "app is back" signals — visibilitychange → visible, window focus,
 * `online`, and bfcache `pageshow` (persisted) — into one debounced resume event. A resume only
 * fires when a matching "away" signal (hidden / blur / offline / pagehide) was seen first and the
 * app was away for at least RESUME_MIN_AWAY_MS, so quick tab switches and the initial page load
 * never trigger it.
 *
 * Cold start (PWA killed and relaunched, or any fresh navigation) never fires: no away signal has
 * been seen in this page's lifetime, and the load already fetched fresh data. A page loaded while
 * hidden (background tab, prerender) counts as away from load, so showing it later than
 * RESUME_MIN_AWAY_MS fires once — its data is that old.
 *
 * iOS standalone PWAs often skip focus/blur: visibilitychange and bfcache `pageshow` (persisted)
 * alone are enough.
 */

export const RESUME_DEBOUNCE_MS = 500;
export const RESUME_MIN_AWAY_MS = 10_000;
/** Consumers refetch data (not just rejoin channels) only past this away duration. */
export const RESUME_REFETCH_AWAY_MS = 30_000;
/** Hard cap: at most one resume per window, whatever the event stream or clock does. */
export const RESUME_MIN_INTERVAL_MS = 2_000;

/** @typedef {"visibility"|"focus"|"online"|"bfcache"} ResumeTrigger */
/** @typedef {{ awayMs: number, trigger: ResumeTrigger, at: number }} ResumeInfo */

/** @type {Set<(info: ResumeInfo) => void>} */
const subscribers = new Set();
/** @type {Array<[EventTarget, string, EventListener]>} */
let listeners = [];
let initialized = false;
/** Timestamp of the first away signal since the last processed resume (null = present). */
let awaySince = null;
/** Pending debounced resume: first trigger + when it arrived. */
let pending = null;
let pendingTimer = null;
/** @type {ResumeInfo | null} */
let lastResume = null;

function isPresent() {
  if (typeof document !== "undefined" && document.visibilityState === "hidden") return false;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
  return true;
}

function markAway() {
  if (awaySince == null) awaySince = Date.now();
}

function processResume() {
  pendingTimer = null;
  const p = pending;
  pending = null;
  if (!p || awaySince == null) return;
  // Left again inside the debounce window: keep the original awaySince for the next return.
  if (!isPresent()) return;
  const awayMs = Math.max(0, p.at - awaySince);
  awaySince = null;
  if (awayMs < RESUME_MIN_AWAY_MS) return;
  // abs(): a wall-clock jump backwards must not suppress resumes until the clock catches up.
  if (lastResume && Math.abs(p.at - lastResume.at) < RESUME_MIN_INTERVAL_MS) return;
  const info = { awayMs, trigger: p.trigger, at: p.at };
  lastResume = info;
  for (const fn of Array.from(subscribers)) {
    try {
      fn(info);
    } catch (err) {
      console.error("[resume] subscriber failed", err);
    }
  }
}

/** @param {ResumeTrigger} trigger */
function noteReturn(trigger) {
  if (awaySince == null) return;
  if (pending) return;
  pending = { trigger, at: Date.now() };
  pendingTimer = setTimeout(processResume, RESUME_DEBOUNCE_MS);
}

function listen(target, type, handler) {
  target.addEventListener(type, handler);
  listeners.push([target, type, handler]);
}

/**
 * Kill switch. NEXT_PUBLIC_* is inlined at build time: flipping it needs a redeploy.
 * Disabled = no listeners, no resume events; consumers keep their non-resume behaviour.
 */
export function isResumeCoordinatorEnabled() {
  return process.env.NEXT_PUBLIC_RESUME_COORDINATOR_ENABLED !== "false";
}

/** Attach global listeners once. Safe to call repeatedly and during SSR (no-op). */
export function initResumeCoordinator() {
  if (initialized || typeof window === "undefined" || typeof document === "undefined") return;
  if (!isResumeCoordinatorEnabled()) return;
  initialized = true;
  if (!isPresent()) markAway();

  listen(document, "visibilitychange", () => {
    if (document.visibilityState === "hidden") markAway();
    else noteReturn("visibility");
  });
  listen(window, "blur", markAway);
  listen(window, "focus", () => noteReturn("focus"));
  listen(window, "offline", markAway);
  listen(window, "online", () => noteReturn("online"));
  listen(window, "pagehide", markAway);
  listen(window, "pageshow", (event) => {
    if (event?.persisted) noteReturn("bfcache");
  });
}

/**
 * @param {(info: ResumeInfo) => void} callback
 * @returns {() => void} unsubscribe (idempotent)
 */
export function onResume(callback) {
  if (typeof callback !== "function") return () => {};
  initResumeCoordinator();
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

/** @returns {{ awayMs: number, trigger: ResumeTrigger | null }} last fired resume (zeros if none yet) */
export function getLastResumeInfo() {
  return lastResume
    ? { awayMs: lastResume.awayMs, trigger: lastResume.trigger }
    : { awayMs: 0, trigger: null };
}

/** Test-only: detach listeners and clear all state. */
export function __resetResumeCoordinatorForTests() {
  for (const [target, type, handler] of listeners) target.removeEventListener(type, handler);
  listeners = [];
  if (pendingTimer) clearTimeout(pendingTimer);
  pendingTimer = null;
  pending = null;
  awaySince = null;
  lastResume = null;
  initialized = false;
  subscribers.clear();
}
